import { useEffect, useState, useCallback } from 'react';
import {
  Header,
  Page,
  Content,
  InfoCard,
  StatusOK,
  StatusRunning,
  StatusPending,
  StatusAborted,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Typography,
  IconButton,
  Tooltip,
  Card,
  Divider,
  Tabs,
  Tab,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';

interface TrackRelease {
  versionCode: number;
  versionName: string;
  status: string;
  rolloutPercent: number;
}

interface TrackInfo {
  trackName: string;
  releases: TrackRelease[];
}

interface TracksResponse {
  packageName: string;
  tracks: TrackInfo[];
}

const appDisplayNames: Record<string, { label: string; icon: JSX.Element }> = {
  'com.appdeuna.wallet': {
    label: 'Personas',
    icon: <AccountBalanceWalletIcon />,
  },
  'com.appdeuna.business': {
    label: 'Negocios',
    icon: <BusinessCenterIcon />,
  },
};

const getAppLabel = (pkg: string) => appDisplayNames[pkg]?.label || pkg;

const trackColors: Record<string, string> = {
  production: '#4caf50',
  beta: '#ff9800',
  alpha: '#2196f3',
  'open-testing': '#ff9800',
  'closed-testing': '#9c27b0',
  internal: '#607d8b',
};

const statusConfig: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  completed: { color: '#2e7d32', bg: '#e8f5e9', label: 'Completed' },
  inProgress: { color: '#e65100', bg: '#fff3e0', label: 'In Progress' },
  draft: { color: '#546e7a', bg: '#eceff1', label: 'Draft' },
  halted: { color: '#c62828', bg: '#ffebee', label: 'Halted' },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appTabs: {
      marginBottom: theme.spacing(3),
      '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
    appTab: {
      minHeight: 56,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.95rem',
      '& .MuiTab-wrapper': {
        flexDirection: 'row',
        gap: theme.spacing(1),
      },
    },
    mainTrackCard: {
      borderRadius: 16,
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: theme.shadows[8],
      },
    },
    mainTrackAccent: {
      height: 5,
      width: '100%',
    },
    mainTrackHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2.5, 3),
    },
    mainTrackName: {
      fontWeight: 800,
      fontSize: '1.4rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    mainReleaseRow: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 3),
      gap: theme.spacing(2.5),
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    mainVersionCode: {
      fontWeight: 700,
      fontSize: '1.3rem',
      fontFamily: 'monospace',
    },
    mainVersionName: {
      fontSize: '0.85rem',
      opacity: 0.6,
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      opacity: 0.5,
      marginBottom: theme.spacing(2),
    },
    trackCard: {
      borderRadius: 12,
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: theme.shadows[6],
      },
    },
    trackAccent: {
      height: 4,
      width: '100%',
    },
    trackHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2.5),
    },
    trackName: {
      fontWeight: 700,
      fontSize: '1.1rem',
      textTransform: 'capitalize',
    },
    releaseRow: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1.5, 2.5),
      gap: theme.spacing(2),
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    versionBox: {
      minWidth: 180,
    },
    versionCode: {
      fontWeight: 700,
      fontSize: '1rem',
      fontFamily: 'monospace',
    },
    versionName: {
      fontSize: '0.8rem',
      opacity: 0.6,
    },
    progressContainer: {
      flex: 1,
      minWidth: 120,
    },
    progressBar: {
      height: 8,
      borderRadius: 4,
    },
    progressLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      marginTop: 2,
      textAlign: 'right',
    },
    statusChip: {
      fontWeight: 600,
      fontSize: '0.75rem',
      borderRadius: 6,
      minWidth: 100,
      justifyContent: 'center',
    },
    emptyState: {
      textAlign: 'center',
      padding: theme.spacing(8, 2),
      opacity: 0.5,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    refreshButton: {
      backgroundColor: 'rgba(255,255,255,0.15)',
      color: '#fff',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.25)',
      },
    },
    releaseCount: {
      fontSize: '0.75rem',
      opacity: 0.7,
      fontWeight: 500,
    },
  }),
);

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed':
      return <StatusOK />;
    case 'inProgress':
      return <StatusRunning />;
    case 'draft':
      return <StatusPending />;
    case 'halted':
      return <StatusAborted />;
    default:
      return <StatusPending />;
  }
};

export const AndroidReleasesPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const [apps, setApps] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [data, setData] = useState<TracksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedApp = apps[selectedTab] || '';

  const fetchApps = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('android-releases');
      const response = await fetchApi(`${baseUrl}/apps`);
      if (response.ok) {
        const json = await response.json();
        setApps(json.apps || []);
      }
    } catch {
      // fallback: single app mode
      setApps(['com.appdeuna.wallet']);
    }
  }, [discoveryApi, fetchApi]);

  const fetchTracks = useCallback(
    async (packageName: string) => {
      if (!packageName) return;
      setLoading(true);
      setError(null);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('android-releases');
        const response = await fetchApi(
          `${baseUrl}/tracks?track=all&package=${encodeURIComponent(
            packageName,
          )}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json: TracksResponse = await response.json();
        if (json.tracks) {
          json.tracks = json.tracks.map(t => ({
            ...t,
            releases: t.releases || [],
          }));
        }
        setData(json);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  useEffect(() => {
    if (selectedApp) {
      fetchTracks(selectedApp);
    }
  }, [selectedApp, fetchTracks]);

  const totalTracks = data?.tracks?.length ?? 0;

  const getTrackColor = (name: string) => {
    const lower = name.toLowerCase();
    return trackColors[lower] || '#607d8b';
  };

  const getStatusStyle = (status: string) =>
    statusConfig[status] || { color: '#546e7a', bg: '#eceff1', label: status };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Page themeId="tool">
      <Header
        title="Android Releases"
        subtitle={
          selectedApp
            ? `${getAppLabel(selectedApp)} — ${selectedApp}`
            : 'Google Play Console'
        }
      >
        <Tooltip title="Refresh data">
          <IconButton
            className={classes.refreshButton}
            onClick={() => fetchTracks(selectedApp)}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Header>
      <Content>
        {apps.length > 1 && (
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            className={classes.appTabs}
            indicatorColor="primary"
            textColor="primary"
          >
            {apps.map(pkg => {
              const display = appDisplayNames[pkg];
              return (
                <Tab
                  key={pkg}
                  className={classes.appTab}
                  icon={display?.icon || <PhoneAndroidIcon />}
                  label={display?.label || pkg}
                />
              );
            })}
          </Tabs>
        )}

        {loading && <LinearProgress />}
        {error && (
          <InfoCard title="Error" variant="fullHeight">
            <Box p={2}>
              <Typography color="error">
                Failed to load tracks: {error}
              </Typography>
            </Box>
          </InfoCard>
        )}

        {!loading && !error && data && (
          <>
            {/* Main Tracks: Production & Internal */}
            <Grid container spacing={3}>
              {(['production', 'internal'] as const).map(trackKey => {
                const track = data.tracks.find(
                  t => t.trackName.toLowerCase() === trackKey,
                );
                const color = getTrackColor(trackKey);
                const label =
                  trackKey === 'production' ? 'Production' : 'Internal';
                const releases = track?.releases || [];
                return (
                  <Grid item xs={12} md={6} key={trackKey}>
                    <Card className={classes.mainTrackCard} variant="outlined">
                      <div
                        className={classes.mainTrackAccent}
                        style={{ backgroundColor: color }}
                      />
                      <div className={classes.mainTrackHeader}>
                        <Typography
                          className={classes.mainTrackName}
                          style={{ color }}
                        >
                          {label}
                        </Typography>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          {releases.some(r => r.status === 'inProgress') && (
                            <Chip
                              label="ROLLING OUT"
                              size="small"
                              style={{
                                backgroundColor: '#fff3e0',
                                color: '#e65100',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                          <Typography className={classes.releaseCount}>
                            {releases.length} release
                            {releases.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </div>
                      <Divider />
                      {releases.length === 0 ? (
                        <Box p={3} textAlign="center">
                          <Typography variant="body2" color="textSecondary">
                            No releases
                          </Typography>
                        </Box>
                      ) : (
                        releases.map((release, idx) => {
                          const statusStyle = getStatusStyle(release.status);
                          return (
                            <div className={classes.mainReleaseRow} key={idx}>
                              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <StatusIcon status={release.status} />
                              </Box>
                              <div className={classes.versionBox}>
                                <Typography className={classes.mainVersionCode}>
                                  {release.versionName || 'unnamed'}
                                </Typography>
                                <Typography className={classes.mainVersionName}>
                                  build {release.versionCode}
                                </Typography>
                              </div>
                              <div className={classes.progressContainer}>
                                <LinearProgress
                                  variant="determinate"
                                  value={release.rolloutPercent}
                                  className={classes.progressBar}
                                  style={{
                                    backgroundColor: `${color}20`,
                                  }}
                                />
                                <Typography
                                  className={classes.progressLabel}
                                  style={{ color }}
                                >
                                  {release.rolloutPercent.toFixed(1)}%
                                </Typography>
                              </div>
                              <Chip
                                label={statusStyle.label}
                                size="small"
                                className={classes.statusChip}
                                style={{
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.color,
                                }}
                              />
                            </div>
                          );
                        })
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Other Tracks */}
            {data.tracks.filter(
              t => !['production', 'internal'].includes(t.trackName.toLowerCase()),
            ).length > 0 && (
              <Box mt={4}>
                <Typography className={classes.sectionTitle}>
                  Otros Tracks
                </Typography>
                <Grid container spacing={2}>
                  {data.tracks
                    .filter(
                      t =>
                        !['production', 'internal'].includes(
                          t.trackName.toLowerCase(),
                        ),
                    )
                    .map(track => (
                      <Grid item xs={12} md={6} lg={4} key={track.trackName}>
                        <Card className={classes.trackCard} variant="outlined">
                          <div
                            className={classes.trackAccent}
                            style={{
                              backgroundColor: getTrackColor(track.trackName),
                            }}
                          />
                          <div className={classes.trackHeader}>
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ gap: 10 }}
                            >
                              <Typography
                                className={classes.trackName}
                                style={{
                                  color: getTrackColor(track.trackName),
                                }}
                              >
                                {track.trackName}
                              </Typography>
                              <Typography className={classes.releaseCount}>
                                {track.releases.length} release
                                {track.releases.length !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          </div>
                          <Divider />
                          {track.releases.length === 0 ? (
                            <Box p={2} textAlign="center">
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                No releases
                              </Typography>
                            </Box>
                          ) : (
                            track.releases.map((release, idx) => {
                              const statusStyle = getStatusStyle(
                                release.status,
                              );
                              return (
                                <div className={classes.releaseRow} key={idx}>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 6 }}
                                  >
                                    <StatusIcon status={release.status} />
                                  </Box>
                                  <div className={classes.versionBox}>
                                    <Typography
                                      className={classes.versionCode}
                                    >
                                      {release.versionName || 'unnamed'}
                                    </Typography>
                                    <Typography
                                      className={classes.versionName}
                                    >
                                      build {release.versionCode}
                                    </Typography>
                                  </div>
                                  <div className={classes.progressContainer}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={release.rolloutPercent}
                                      className={classes.progressBar}
                                      style={{
                                        backgroundColor: `${getTrackColor(
                                          track.trackName,
                                        )}20`,
                                      }}
                                    />
                                    <Typography
                                      className={classes.progressLabel}
                                      style={{
                                        color: getTrackColor(track.trackName),
                                      }}
                                    >
                                      {release.rolloutPercent.toFixed(1)}%
                                    </Typography>
                                  </div>
                                  <Chip
                                    label={statusStyle.label}
                                    size="small"
                                    className={classes.statusChip}
                                    style={{
                                      backgroundColor: statusStyle.bg,
                                      color: statusStyle.color,
                                    }}
                                  />
                                </div>
                              );
                            })
                          )}
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {totalTracks === 0 && (
              <Box className={classes.emptyState}>
                <PhoneAndroidIcon style={{ fontSize: 64 }} />
                <Typography variant="h6">No tracks found</Typography>
                <Typography variant="body2">
                  No Google Play tracks available for this package.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
